import {
  ConversationDraftSendOptionSection,
  ConversationDraftSideEffectActionType,
} from '@provision/conversation-types';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  DraftSchedule,
  DraftScheduleSendAndAction,
  DraftScheduleSendUnless,
  isScheduleEmpty,
} from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { SendOptionItem } from 'src/pages/Conversations/Chat/InputField/send-options-button';
import { useCurrentDateTimer } from 'src/utils/hooks/use-current-date-timer';

type DraftScheduleUpdater = (
  schedule: Partial<DraftSchedule>,
  options?: { allowEmpty?: boolean },
) => Promise<unknown> | undefined;

type DraftScheduleSubmit = (params: {
  scheduledAt: string;
  sendUnless: DraftScheduleSendUnless[];
  mode?: 'edit' | 'replace';
}) => Promise<void> | undefined;

interface UseDraftSchedulingParams {
  scheduledDraftSchedule: DraftSchedule;
  displayTime: (value: string | dayjs.Dayjs | Date) => string;
  flushDraft: () => Promise<unknown> | undefined;
  updateDraftSchedule: DraftScheduleUpdater;
  executeSendAndActions: (actions: DraftScheduleSendAndAction[]) => Promise<void>;
  syncScheduledDraft: (schedule?: DraftSchedule) => Promise<void>;
  unscheduleDraft: () => Promise<void> | void;
}

export interface UseDraftSchedulingResult {
  hasScheduledMessage: boolean;
  scheduledLabel: string;
  scheduledDiscardedLabel: string;
  isScheduleDiscarded: boolean;
  sendOptions: SendOptionItem[];
  isScheduleModalOpen: boolean;
  openScheduleModal: () => void;
  closeScheduleModal: () => void;
  handleSendOptionSelect: (optionId: string) => Promise<void>;
  handleScheduleModalSubmit: DraftScheduleSubmit;
  handleDiscardScheduledMessage: () => void;
  handleViewScheduledMessage: () => void;
  handleDoneViewingScheduledMessage: () => void;
  handleReschedule: () => void;
  isViewingScheduledMessage: boolean;
  isApplyingSendOption: boolean;
  scheduledConditions: DraftScheduleSendUnless[] | null;
}

const SEND_AND_SOLVE: DraftScheduleSendAndAction = {
  type: ConversationDraftSideEffectActionType.Status,
  value: 'solved',
};

const createSnoozeAction = (
  offset: number,
  unit: dayjs.ManipulateType,
): DraftScheduleSendAndAction =>
  ({
    type: ConversationDraftSideEffectActionType.Snooze,
    value: dayjs()
      .add(offset, unit)
      .set('hour', 9)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0)
      .toISOString(),
  }) satisfies DraftScheduleSendAndAction;

const createSnoozeUntilAction = (iso: string): DraftScheduleSendAndAction | null => {
  const parsed = dayjs(iso);
  if (!parsed.isValid() || !parsed.isAfter(dayjs())) {
    return null;
  }

  return {
    type: ConversationDraftSideEffectActionType.Snooze,
    value: parsed.toISOString(),
  } satisfies DraftScheduleSendAndAction;
};

export function useDraftScheduling({
  scheduledDraftSchedule,
  displayTime,
  flushDraft,
  updateDraftSchedule,
  executeSendAndActions,
  syncScheduledDraft,
  unscheduleDraft,
}: UseDraftSchedulingParams): UseDraftSchedulingResult {
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isViewingScheduledMessage, setIsViewingScheduledMessage] = useState(false);
  const [isApplyingSendOption, setIsApplyingSendOption] = useState(false);
  const hasScheduledMessage = useMemo(
    () => !isScheduleEmpty(scheduledDraftSchedule),
    [scheduledDraftSchedule],
  );
  const currentDatTimer = useCurrentDateTimer();

  const scheduledDiscardedAt = scheduledDraftSchedule.discardedAt ?? null;
  const isScheduleDiscarded = Boolean(scheduledDiscardedAt);

  const scheduledConditions = useMemo(
    () => scheduledDraftSchedule.sendUnless?.map((condition) => ({ ...condition })) ?? null,
    [scheduledDraftSchedule],
  );

  useEffect(() => {
    if (!hasScheduledMessage) {
      setIsViewingScheduledMessage(false);
    }
  }, [hasScheduledMessage]);

  const scheduledLabel = useMemo(() => {
    if (!scheduledDraftSchedule?.scheduledAt) {
      return '';
    }
    const scheduledDate = dayjs(scheduledDraftSchedule.scheduledAt);
    if (!scheduledDate.isValid()) {
      return '';
    }
    const datePart = scheduledDate.format('dddd, MMMM D');
    return `${datePart} at ${displayTime(scheduledDate)}`;
  }, [displayTime, scheduledDraftSchedule?.scheduledAt]);

  const scheduledDiscardedLabel = useMemo(() => {
    if (!scheduledDiscardedAt) {
      return '';
    }

    const discardedDate = dayjs(scheduledDiscardedAt);

    if (!discardedDate.isValid()) {
      return '';
    }

    const datePart = discardedDate.format('MMM D');

    return `${datePart} at ${displayTime(discardedDate)}`;
  }, [displayTime, scheduledDiscardedAt]);

  const sendOptions = useMemo<SendOptionItem[]>(() => {
    const hourFromNow = dayjs().add(1, 'hour');
    const tomorrowMorning = dayjs()
      .add(1, 'day')
      .set('hour', 9)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);

    const nextWeek = dayjs()
      .add(1, 'week')
      .set('hour', 9)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);

    const nextMonth = dayjs()
      .add(1, 'month')
      .set('hour', 9)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);

    const describe = (value: dayjs.Dayjs): string => {
      const dayPart = value.format('ddd, MMM D');
      return `${dayPart} at ${displayTime(value)}`;
    };

    const baseItems = [
      {
        id: 'send-and-solve',
        label: 'Send and Solve',
        section: ConversationDraftSendOptionSection.SendAnd,
        icon: 'task_alt',
      },
      {
        id: 'send-and-snooze-tomorrow',
        label: 'Send and Snooze until tomorrow',
        section: ConversationDraftSendOptionSection.SendAnd,
        icon: 'snooze',
      },
      {
        id: 'send-and-snooze-week',
        label: 'Send and Snooze for a week',
        section: ConversationDraftSendOptionSection.SendAnd,
        icon: 'snooze',
      },
    ] as SendOptionItem[];

    if (hasScheduledMessage) {
      return [
        ...baseItems,
        {
          id: 'send-later-custom',
          label: 'Edit scheduled message time',
          section: ConversationDraftSendOptionSection.SendLater,
          icon: 'edit_calendar',
        },
      ] as SendOptionItem[];
    }

    return [
      ...baseItems,
      {
        id: 'send-later-hour',
        label: 'Send in an hour',
        section: ConversationDraftSendOptionSection.SendLater,
        description: describe(hourFromNow),
        icon: 'schedule_send',
      },
      {
        id: 'send-later-tomorrow',
        label: 'Send tomorrow at 9am',
        section: ConversationDraftSendOptionSection.SendLater,
        description: describe(tomorrowMorning),
        icon: 'calendar_month',
      },
      {
        id: 'send-later-week',
        label: 'Send next week',
        section: ConversationDraftSendOptionSection.SendLater,
        description: describe(nextWeek),
        icon: 'calendar_month',
      },
      {
        id: 'send-later-month',
        label: 'Send next month',
        section: ConversationDraftSendOptionSection.SendLater,
        description: describe(nextMonth),
        icon: 'calendar_month',
      },
      {
        id: 'send-later-custom',
        label: 'Custom schedule',
        section: ConversationDraftSendOptionSection.SendLater,
        icon: 'edit_calendar',
      },
    ] as SendOptionItem[];
    // eslint-disable-next-line
  }, [displayTime, hasScheduledMessage, currentDatTimer]);

  const openScheduleModal = useCallback((): void => setIsScheduleModalOpen(true), []);
  const closeScheduleModal = useCallback((): void => setIsScheduleModalOpen(false), []);

  const applySchedule = useCallback(
    async (
      scheduledAt: string,
      sendUnless: DraftScheduleSendUnless[] | null,
      opts?: { forceSync?: boolean },
    ): Promise<void> => {
      const hadScheduledMessage = hasScheduledMessage;
      const schedule: DraftSchedule = {
        scheduledAt,
        sendUnless: sendUnless?.length ? sendUnless : null,
        sideEffects: null,
        discardedAt: null,
      };

      await flushDraft();
      await updateDraftSchedule(schedule, { allowEmpty: true });

      if (!hadScheduledMessage || opts?.forceSync) {
        await syncScheduledDraft(schedule);
      }

      setIsScheduleModalOpen(false);
      setIsViewingScheduledMessage(false);
    },
    [flushDraft, hasScheduledMessage, syncScheduledDraft, updateDraftSchedule],
  );

  const schedulePreset = useCallback(
    async (compute: () => dayjs.Dayjs): Promise<void> => {
      const target = compute();
      if (!target.isAfter(dayjs())) {
        setIsScheduleModalOpen(true);
        return;
      }
      await applySchedule(target.toISOString(), scheduledDraftSchedule.sendUnless);
    },
    [applySchedule, scheduledDraftSchedule.sendUnless],
  );

  const executeSendWithActions = useCallback(
    async (actions: DraftScheduleSendAndAction[]): Promise<void> => {
      if (!actions.length) {
        return;
      }
      setIsApplyingSendOption(true);
      try {
        await executeSendAndActions(actions);
      } finally {
        setIsApplyingSendOption(false);
      }
    },
    [executeSendAndActions],
  );

  const handleSendOptionSelect = useCallback(
    async (optionId: string): Promise<void> => {
      if (optionId.startsWith('send-and-snooze-until:')) {
        const encodedIso = optionId.slice('send-and-snooze-until:'.length);
        try {
          const iso = decodeURIComponent(encodedIso);
          const action = createSnoozeUntilAction(iso);
          if (action) {
            await executeSendWithActions([action]);
          }
        } catch {
          // ignore malformed iso values
        }
        return;
      }

      if (optionId.startsWith('send-and-snooze:')) {
        const [, amountRaw, unitRaw] = optionId.split(':');
        const amount = Number.parseInt(amountRaw ?? '', 10);
        const unit = unitRaw as dayjs.ManipulateType;
        if (Number.isFinite(amount) && amount > 0 && unit) {
          await executeSendWithActions([createSnoozeAction(amount, unit)]);
        }
        return;
      }

      switch (optionId) {
        case 'send-and-solve':
          await executeSendWithActions([SEND_AND_SOLVE]);
          break;
        case 'send-and-snooze-tomorrow':
          await executeSendWithActions([createSnoozeAction(1, 'day')]);
          break;
        case 'send-and-snooze-week':
          await executeSendWithActions([createSnoozeAction(1, 'week')]);
          break;
        case 'send-later-hour':
          await schedulePreset(() => dayjs().add(1, 'hour'));
          break;
        case 'send-later-tomorrow':
          await schedulePreset(() =>
            dayjs()
              .add(1, 'day')
              .set('hour', 9)
              .set('minute', 0)
              .set('second', 0)
              .set('millisecond', 0),
          );
          break;
        case 'send-later-week':
          await schedulePreset(() =>
            dayjs()
              .add(1, 'week')
              .set('hour', 9)
              .set('minute', 0)
              .set('second', 0)
              .set('millisecond', 0),
          );
          break;
        case 'send-later-month':
          await schedulePreset(() =>
            dayjs()
              .add(1, 'month')
              .set('hour', 9)
              .set('minute', 0)
              .set('second', 0)
              .set('millisecond', 0),
          );
          break;
        case 'send-later-custom':
          setIsScheduleModalOpen(true);
          break;
        default:
          break;
      }
    },
    [executeSendWithActions, schedulePreset],
  );

  const handleScheduleModalSubmit = useCallback<DraftScheduleSubmit>(
    async ({ scheduledAt, sendUnless, mode }) => {
      const forceSync = mode === 'replace';
      await applySchedule(scheduledAt, sendUnless, { forceSync });
    },
    [applySchedule],
  );

  const handleDiscardScheduledMessage = useCallback((): void => {
    setIsScheduleModalOpen(false);
    setIsViewingScheduledMessage(false);
    void unscheduleDraft();
  }, [unscheduleDraft]);

  const handleViewScheduledMessage = useCallback((): void => {
    setIsViewingScheduledMessage(true);
  }, []);

  const handleDoneViewingScheduledMessage = useCallback((): void => {
    setIsViewingScheduledMessage(false);
  }, []);

  const handleReschedule = openScheduleModal;

  return {
    hasScheduledMessage,
    scheduledLabel,
    scheduledDiscardedLabel,
    isScheduleDiscarded,
    sendOptions,
    isScheduleModalOpen,
    openScheduleModal,
    closeScheduleModal,
    handleSendOptionSelect,
    handleScheduleModalSubmit,
    handleDiscardScheduledMessage,
    handleViewScheduledMessage,
    handleDoneViewingScheduledMessage,
    handleReschedule,
    isViewingScheduledMessage,
    isApplyingSendOption,
    scheduledConditions,
  };
}
